// @flow
import React, { Component } from 'react';
import moment from 'moment';
import styled, { withTheme } from 'styled-components';
import { DateRange } from 'react-date-range';
import { translate } from 'react-i18next';

import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import Popover from 'material-ui/Popover'; // TODO: check for issue about popover https://github.com/mui-org/material-ui/issues/8040
import Menu from 'material-ui/Menu';
import ArrowDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import * as Week from 'common/domain/week';

const PickerPopover = styled.div`
  display: flex;
  flex-flow: column;
  padding: 1px;
  width: 576px;
  background-color: transparent;
`;

const dateRangeTheme = (theme) => {
  return {
    Day: {
      width: theme.metrics.icon.small.width,
      height: theme.metrics.icon.small.height,
    },

    DaySelected: {
      background: theme.colors.primary,
    },

    DayInRange: {
      color: theme.colors.primaryText,
      background: theme.colors.secondaryBackground,
    },

    MonthButton: {
      background: 'none',
    },
    Calendar: {
      padding: '10px 9px',
    },
  };
};

type Props = {
  fontSize: number; // material-ui prevents us from changing fontSize otherwise
  from: moment;
  to: moment;
  weekOnly: ?boolean;
  labelStyle?: any;
  disabled: boolean;
  shortMonths: boolean;

  minDate?: moment;
  maxDate?: moment;

  onDateRangeChange: ({ from: moment, to: moment }) => void;

  theme: Object;
  t: any;
}

type State = {
  from: moment;
  to: moment;

  invertDates: boolean;

  opened?: boolean;
  anchor?: any;
}

class DateRangePicker extends Component<Props, State> {

  static defaultProps = {
    fontSize: 22,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      from: props.from,
      to: props.to,
      invertDates: Boolean(props.weekOnly),
    };
  }

  onClick = (event: any) => {
    event.preventDefault();

    this.setState({
      opened: true,
      anchor: event.currentTarget,
    });
  };

  onClose = () => {
    this.setState({
      ...this.state,
      opened: false,
    });
  };

  onChange = (range: any) => {
    const { weekOnly } = this.props;
    const { invertDates } = this.state;
    const { startDate, endDate } = range;

    const dateRange = { from: startDate, to: endDate };
    const { from, to } = weekOnly
      ? Week.asEntireWeeks(dateRange)
      : dateRange;

    // Inverts the dates every other time to make sure the DateRange component updates its internal state
    this.setState({
      from: invertDates ? to : from,
      to: invertDates ? from : to,
      invertDates: weekOnly ? !invertDates : false,
    });
  };

  getDatesOrdered = () => {
    const { from, to } = this.state;

    // Makes sure the dates are in the right order since every other time the dates are inverted in "weekOnly" mode
    const startDate = from.isAfter(to) ? to : from;
    const endDate = from.isAfter(to) ? from : to;

    return { from: startDate, to: endDate };
  };

  onRangeChanged = () => {
    const { from, to } = this.getDatesOrdered();

    this.onClose();
    this.props.onDateRangeChange({ from, to });
  };

  render() {
    const { theme, t, labelStyle, disabled, shortMonths } = this.props;
    const { from, to, opened, anchor } = this.state;

    const { from: startDate, to: endDate } = this.getDatesOrdered();

    const icon = !disabled ? <ArrowDown /> : null;

    const label = shortMonths ?
      t('date.dateRangeShortMonth', { from: startDate, to: endDate, separator: ' - ' }) :
      `${t('date.text', { value: startDate })} - ${t('date.text', { value: endDate })}`;

    return (
      <React.Fragment>
        <FlatButton
          label={label}
          labelPosition="before"
          onClick={this.onClick}
          disabled={disabled}
          labelStyle={{
            ...theme.fonts.style.secondary,
            fontSize: this.props.fontSize,
            textTransform: 'none',
            color: theme.colors.primaryText,
            paddingLeft: '10px',
            ...labelStyle || {},
          }}
          icon={icon}
        />
        { !disabled &&
          <Popover
            open={opened}
            anchorEl={anchor}
            anchorOrigin={{ horizontal: 'middle', vertical: 'bottom' }}
            targetOrigin={{ horizontal: 'middle', vertical: 'top' }}
            onRequestClose={this.onRangeChanged}
            style={{ backgroundColor: 'transparent' }}
          >
            <Menu desktop listStyle={{ paddingTop: 0, paddingBottom: 0 }}>
              <PickerPopover>
                <DateRange
                  startDate={from}
                  endDate={to}
                  minDate={this.props.minDate}
                  maxDate={this.props.maxDate}
                  onChange={this.onChange}
                  theme={dateRangeTheme(theme)}
                  firstDayOfWeek={Week.WEEK_START}
                  linkedCalendars
                  style={{ textAlign: 'center' }}
                />
                <RaisedButton
                  label={t('ok')}
                  primary
                  onClick={this.onRangeChanged}
                />
              </PickerPopover>
            </Menu>
          </Popover>
        }
      </React.Fragment>
    );
  }
}

export default withTheme(translate()(DateRangePicker));



// WEBPACK FOOTER //
// ./src/common/components/DateRangePicker.js